/**
 * Client store for the Team screen.
 *
 * Holds the member list, the summary-tile figures, and the list's UI state
 * (search text, page, page size, filters). There is no POS team endpoint yet,
 * so rows are seeded from the typed fixture through the SSR seam and hydrated
 * here by TeamInitializer — the slice itself never fetches. When the real
 * endpoint lands, only the SSR fetcher changes.
 *
 * Per docs/frontend-conventions.md §9, feature-wide UI state belongs in Redux,
 * not component `useState`.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import {
  EMPTY_TEAM_FILTERS,
  type TeamFilters,
} from "@/components/team/filter/teamFilterOptions";
import type {
  TeamMember,
  TeamMemberPage,
  TeamSummary,
} from "@/components/team/types";

/**
 * Which overlay is on screen. Only one can be open at a time, so this is a
 * single discriminator rather than a boolean per drawer.
 */
export type TeamOverlay =
  | "view"
  | "edit"
  | "add"
  | "draft"
  | "reset-pin"
  | "deactivate"
  | "activate";

const EMPTY_SUMMARY: TeamSummary = {
  totalStaff: 0,
  onDuty: 0,
  onDutyPercent: 0,
  offDuty: 0,
  onLeave: 0,
};

/**
 * The list state the URL carries — what `TeamInitializer` adopts on mount.
 *
 * Mirrors the search params `TeamQuerySync` writes, so the two halves of the
 * round trip cannot drift.
 */
export interface TeamListQuery {
  search: string;
  filters: TeamFilters;
  page: number;
  pageSize: number;
}

export interface TeamState {
  members: TeamMember[];
  /** The record the open drawer is showing — see `setActiveMember`. */
  activeMember: TeamMember | null;
  summary: TeamSummary;
  /** Total rows across all pages — drives "1-10 of 180". */
  total: number;
  page: number;
  pageSize: number;
  search: string;
  filters: TeamFilters;
  /** Open overlay, or null when the list is uncovered. */
  overlay: TeamOverlay | null;
  /** Member the open overlay acts on — null for "add" and "draft". */
  activeMemberId: string | null;
  /** A list navigation is in flight — see `setTeamListPending`. */
  listPending: boolean;
}

const initialState: TeamState = {
  members: [],
  activeMember: null,
  summary: EMPTY_SUMMARY,
  total: 0,
  page: 1,
  pageSize: 10,
  search: "",
  filters: EMPTY_TEAM_FILTERS,
  overlay: null,
  activeMemberId: null,
  listPending: false,
};

const teamSlice = createSlice({
  name: "team",
  initialState,
  reducers: {
    /**
     * True from the moment a query change starts navigating until the server
     * render lands. Set by `TeamQuerySync`, which owns the transition; read by
     * the list, which is a sibling and cannot see it any other way.
     */
    setTeamListPending(state, action: PayloadAction<boolean>) {
      state.listPending = action.payload;
    },
    /** Replace the list with a server-rendered page (called by TeamInitializer). */
    /**
     * The fully-read record the open drawer is showing.
     *
     * `GET staff/` serialises fourteen fields and `GET staff/{id}/` eighteen —
     * `hire_date`, `employment_type`, `permissions` and `photo_url` are
     * **detail-only**, so a roster row cannot fill either drawer. Clicking Edit
     * or View reads the record and puts it here; the drawer renders from this
     * and nothing else.
     *
     * Held apart from `members` rather than merged into it: the roster is a
     * page of the list read and should stay exactly what the server sent, and
     * a **draft has no row at all** to merge into (drafts are hidden from the
     * roster, `staff-directory.md` §1).
     */
    setActiveMember(state, action: PayloadAction<TeamMember>) {
      state.activeMember = action.payload;
    },

    setTeamMembers(state, action: PayloadAction<TeamMemberPage>) {
      state.members = action.payload.members;
      state.summary = action.payload.summary;
      state.total = action.payload.total;
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
    },
    /**
     * Adopt the URL's list state verbatim, on mount.
     *
     * **Not the individual setters**, which reset `page` to 1 — right when a
     * user changes a facet, wrong here: it would drop anyone landing on
     * `?page=3&department=kitchen` back to the first page.
     *
     * Without this the URL is write-only: `TeamQuerySync` rebuilds the query
     * from this slice, so a fresh store — a reload, or a link someone shared —
     * wipes the params the page was just rendered with.
     */
    setTeamListQuery(state, action: PayloadAction<TeamListQuery>) {
      state.search = action.payload.search;
      state.filters = action.payload.filters;
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
    },
    setTeamPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setTeamPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      // A larger page size can put the current page out of range
      state.page = 1;
    },
    setTeamSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
      // Any new query restarts paging from the first page
      state.page = 1;
    },
    setTeamFilters(state, action: PayloadAction<TeamFilters>) {
      state.filters = action.payload;
      state.page = 1;
    },
    /** Open an overlay. `memberId` is required for row-scoped overlays. */
    openTeamOverlay(
      state,
      action: PayloadAction<{ overlay: TeamOverlay; memberId?: string }>,
    ) {
      state.overlay = action.payload.overlay;
      // A different row means the record in hand is the wrong one
      if (state.activeMember?.id !== action.payload.memberId) {
        state.activeMember = null;
      }
      state.activeMemberId = action.payload.memberId ?? null;
    },
    closeTeamOverlay(state) {
      state.overlay = null;
      // Cleared with the drawer, so the next person never opens onto the
      // previous one's permissions while their own read is in flight
      state.activeMember = null;
      state.activeMemberId = null;
    },
    clearTeamFilters(state) {
      state.filters = EMPTY_TEAM_FILTERS;
      state.page = 1;
    },
    /**
     * Flip a member's active flag (Figma 2325:127718). Store-only — no POS team
     * endpoint exists yet, so this is what the profile re-reads to switch
     * between its normal and deactivated presentation.
     */
    setTeamMemberActive(
      state,
      action: PayloadAction<{ memberId: string; active: boolean }>,
    ) {
      const member = state.members.find(
        (row) => row.id === action.payload.memberId,
      );
      if (!member) return;
      member.active = action.payload.active;
      /**
       * **Both fields, or the roster lies.** `active` drives the details
       * drawer's deactivated variant and `lifecycleStatus` drives the table's
       * Status badge — writing only the first left the badge reading "Active"
       * until the server round-trip landed, which is the same lag QA reported
       * on the drawer. `draft` is never a target here: the lifecycle routes
       * refuse a draft outright (`409 draft_not_deactivatable`).
       */
      member.lifecycleStatus = action.payload.active ? "active" : "inactive";
    },
  },
});

export const {
  setTeamListPending,
  setTeamMembers,
  setActiveMember,
  setTeamListQuery,
  setTeamPage,
  setTeamPageSize,
  setTeamSearch,
  setTeamFilters,
  clearTeamFilters,
  openTeamOverlay,
  closeTeamOverlay,
  setTeamMemberActive,
} = teamSlice.actions;

export default teamSlice.reducer;
